import r2Core, {coreCrypto} from '@core/r2';
import { IChatMessage, IChatMessageBotEmbed, type IChatMessageResult } from "@listat/r2-core";
import { IChatActionsData } from "@core/base/chat-action-base";
import { OfficeChatRepository } from "@core/office/office-chat-repository/office-chat-repository";
import { OfficeChatAdapterV1Repository } from "@core/office/office-chat/office-chat-v1/office-chat-adapter-v1-repository";
import { IChatOfficeMessage, IChatOfficeMessageBot } from "@core/office/office-chat/office-chat-v1/office-chat-v1.types";
import { ISessionInfo } from "@core/base/session-base.types";
import { Observable, map, BehaviorSubject } from "rxjs";
import { OfficeChatV1 } from "./office-chat-v1";
import {OfficeChatBase} from "@core/office/office-chat/office-chat-base";
import {logger} from "@services/logger/logger.service";
import { ICoreDependencies } from "@core/core-boot/core-boot.types";
import { OfficeChatSetting } from "@core/office/office-chat-setting/office-chat-setting";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import { ChatUtilsService } from "@core/shared/chat-utils.service";
import { OfficeService } from "@core/office/office-service/office-service";

const coreConstants = r2Core.constants.chat;

export class OfficeChatAdapterServiceV1 extends OfficeChatV1 implements OfficeChatBase {
    public constants = r2Core.constants.chat;
    public cryptoServiceRepository = new coreCrypto.Channel()
    private chatRepositoryCrypto = new OfficeChatAdapterV1Repository(this.chatUtils, this.cryptoServiceRepository);
    private isMessagesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)
    private officeService = this.office

    constructor(
        private dependencies: ICoreDependencies,
        private office: OfficeService,
        private chatRepository: OfficeChatRepository,
        private chatUtils: ChatUtilsService,
        settingService: OfficeChatSetting,
        sessionService: OfficeSessionService,
    ) {
        super(
            dependencies.http,
            settingService,
            sessionService
        );
        this.defineHandlerForDocumentChange();
        this.defineHandlerForSessionChange();
    }

    public $isMessagesLoading(): Observable<boolean> {
        return this.isMessagesLoading.asObservable()
    }

    public getMessages(): Observable<IChatMessage[]> {
        return this.$messages.pipe(map(data => {
            return this.convertOfficeMessagesToMessages(data);
        }));
    }

    public getMessagesValue(): IChatMessage[] {
        const officeMessages = this.$messages.getValue();
        return this.convertOfficeMessagesToMessages(officeMessages);
    }

    public async sendMessage(text: string, dataAction: IChatActionsData | null): Promise<IChatMessageResult> {
        const result = await super.send(text, dataAction);
        if (result.isError) {
            return result;
        }
        this.saveMessagesToRepository();
        return result;
    }


    public async sendMessageWithoutSaving(text: string, dataAction: IChatActionsData | null): Promise<IChatMessageResult> {
        const result = await super.send(text, dataAction, false);
        if (result.isError) {
            return result;
        }
        this.saveMessagesToRepository();
        return result;
    }

    public async clearMessages(): Promise<void> {
        this.$messages.next([]);
        await this.chatRepository.deleteMessages();

    }

    public async writeBotMessage(text: string): Promise<void> {
        const botMessage = super.createBotMessage(text, false);
        const newMessages = this.$messages.getValue();
        newMessages.push(botMessage);
        this.$messages.next(newMessages);
        this.saveMessagesToRepository();
    }

    public async init(): Promise<void> {
        await this.initCryptoServiceRepository()
        await this.initData();
    }

    private convertOfficeMessagesToMessages(data: IChatOfficeMessage[]): IChatMessage[] {
        return data.map(item => {
            if (item.sender === coreConstants.MESSAGE_SENDER.user) {
                return {
                    value: item.value,
                    sender: coreConstants.MESSAGE_SENDER.user,
                    metadata: item.metadata,
                };
            }

            const embeds = this.searchEmbeds(item);

            return {
                value: item.isImage ? '' : item.value,
                sender: coreConstants.MESSAGE_SENDER.bot,
                options: {
                    plan: {
                        availableTokens: 0,
                        type: coreConstants.SETTING_PLAN_TYPE.TRIAL,
                    },
                    embeds,
                    usedTotalTokens: 0,
                    usages: []
                }
            };
        });
    }

    private convertMessagesToOfficeMessages(data: IChatMessage[]): IChatOfficeMessage[] {
        return data.map(item => {
            if (item.sender === coreConstants.MESSAGE_SENDER.user) {
                return {
                    value: item.value,
                    sender: coreConstants.MESSAGE_SENDER.user,
                    metadata: item.metadata,
                };
            }

            const isValueImg = this.chatUtils.isValueImg(item);
            const value = isValueImg ? this.chatUtils.firstEmbeds(item).image.content : item.value;
            return {
                value: value,
                sender: item.sender,
                isImage: isValueImg,
            };
        });
    }

    private searchEmbeds(item: IChatOfficeMessageBot): IChatMessageBotEmbed[] {
        if (item.isImage) {
            return [{
                image: {
                    content: item.value,
                    url: '',
                    mimeType: '',
                }
            }];
        }

        return [];
    }

    private async initRepository(): Promise<void> {
        const session = this.sessionService.getSession();
        const documentID = await this.officeService.getHost().getDocumentIDAsync();

        if (session.user_id && documentID) {
            this.chatRepository.defineHistoryKey(session.user_id, documentID);
        }
    }

    private async saveMessagesToRepository(): Promise<void> {
        const chatMessagesOld = this.$messages.getValue();
        const chatMessages = this.convertOfficeMessagesToMessages(chatMessagesOld);
        const encryptedMessages = await this.chatRepositoryCrypto.encryptMessages(chatMessages);
        await this.chatRepository.writeMessages(encryptedMessages);
    }

    private async initData(): Promise<void> {
        // this.isMessagesLoading.next(true)
        await this.initRepository();
        await this.initOfficeMessages();
        this.isMessagesLoading.next(false)
    }

    private async initOfficeMessages(): Promise<void> {
        try {
            const messages = await this.chatRepository.readMessages();
            const decryptedMessages = await this.chatRepositoryCrypto.decryptMessages(messages);
            const officeMessages = this.convertMessagesToOfficeMessages(decryptedMessages);
            this.$messages.next(officeMessages);
        } catch (e) {
            logger.error(e);
            this.$messages.next([]);
        }
    }

    private defineHandlerForDocumentChange(): void {
        const host = this.officeService.getHost();
        host.onDocumentChange(this.initData.bind(this));
    }

    private defineHandlerForSessionChange(): void {
        this.sessionService.sessionInfo$.subscribe(
            this.handlerForSessionChange.bind(this)
        );
    }

    private handlerForSessionChange(info: ISessionInfo | null): void {
        if (info && info.user_id) {
            logger.log(info, info.user_id)
            this.init()
        }
    }

    private async initCryptoServiceRepository(): Promise<void> {
        const sessionInfo = this.sessionService.getSession();
        await this.cryptoServiceRepository.ApplySecurityCode(`chatRepository_${sessionInfo.user_id}`)

        // this.sessionService.sessionInfo$.subscribe(async (sessionInfo) => {
        //     if (sessionInfo) {
        //         await this.cryptoServiceRepository.ApplySecurityCode(`chatRepository_${sessionInfo.user_id}`)
        //     }
        // });
    }

    onDestroy(): void {}
}
